import { useState, useEffect } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline'

import AmountInput from '../components/AmountInput'
import DatePicker from '../components/DatePicker'
import ExpenseTypePicker from '../components/ExpenseTypePicker'
import { expenseAPI, expenseTypeAPI, walletAPI } from '../services/api'
import { formatCurrency } from '../common/currency'
import { formatDate, toDateInputValue } from '../common/date'
import { useI18n } from '../contexts/I18nContext'
import type { ExpenseType } from '../types/expense'
import type { Wallet } from '../types/wallet'

interface ExpenseDraftFormState {
  expense_type_id: string
  wallet_id: string
  amount: string
  date: string
  note: string
}

interface ExpenseFormLocationState {
  returnTo?: string
  expenseDraft?: ExpenseDraftFormState
  createdExpenseTypeId?: number
}

interface ToastState {
  kind: 'success' | 'error'
  message: string
}

export default function ExpenseFormPage() {
  const { t } = useI18n()
  const location = useLocation()
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const isEdit = Boolean(id)
  const navigationState = location.state as ExpenseFormLocationState | null
  const returnTo = !isEdit ? navigationState?.returnTo ?? '/expenses' : '/expenses'
  const returnPath = `${location.pathname}${location.search}`

  const [form, setForm] = useState({
    expense_type_id: '' as string,
    wallet_id: '' as string,
    amount: '',
    date: toDateInputValue(new Date()),
    note: '',
  })
  const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([])
  const [wallets, setWallets] = useState<Wallet[]>([])
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(isEdit)
  const [updatingDefaultAmount, setUpdatingDefaultAmount] = useState(false)
  const [error, setError] = useState('')
  const [toast, setToast] = useState<ToastState | null>(null)
  const [walletPickerOpen, setWalletPickerOpen] = useState(!isEdit)

  useEffect(() => {
    expenseTypeAPI
      .list({ includeStopped: false })
      .then((r) => setExpenseTypes(r.expense_types))
      .catch((err) => { console.error(err) })
    walletAPI
      .list({ includeStopped: false })
      .then((r) => setWallets(r.wallets))
      .catch((err) => { console.error(err) })
  }, [])

  useEffect(() => {
    if (!navigationState?.expenseDraft) return

    setForm(navigationState.expenseDraft)
    setWalletPickerOpen(!navigationState.expenseDraft.wallet_id)
    if (isEdit) {
      setInitialLoading(false)
    }
  }, [isEdit, navigationState])

  useEffect(() => {
    if (!toast) return

    const timeoutId = window.setTimeout(() => {
      setToast(null)
    }, 3000)

    return () => window.clearTimeout(timeoutId)
  }, [toast])

  // Apply query params for preselection (expense_type_id, wallet_id, date)
  useEffect(() => {
    if (isEdit || navigationState?.expenseDraft) return
    const typeId = searchParams.get('expense_type_id')
    const walletId = searchParams.get('wallet_id')
    const date = searchParams.get('date')
    const amount = searchParams.get('amount')

    if (typeId) {
      if (expenseTypes.length > 0) {
        handleExpenseTypeSelect(typeId)
      }
    }
    if (walletId) {
      set('wallet_id', walletId)
      setWalletPickerOpen(false)
    }
    if (date) set('date', date)
    if (amount !== null) set('amount', amount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, searchParams, expenseTypes, navigationState]) // re-run when expenseTypes load

  useEffect(() => {
    if (!isEdit || !id || navigationState?.expenseDraft) return
    setInitialLoading(true)
    expenseAPI
      .get(Number(id))
      .then((exp) => {
        setForm({
          expense_type_id: exp.expense_type_id.toString(),
          wallet_id: exp.wallet_id?.toString() ?? '',
          amount: Math.round(exp.amount).toString(),
          date: toDateInputValue(exp.date),
          note: exp.note,
        })
        setWalletPickerOpen(false)
      })
        .catch(() => setError(t('Failed to load expense')))
      .finally(() => setInitialLoading(false))
      }, [id, isEdit, navigationState, t])

  function handleExpenseTypeSelect(typeId: string) {
    set('expense_type_id', typeId)
    if (isEdit || !typeId) return
    const et = expenseTypes.find((t) => t.id === Number(typeId))
    if (!et) return
    if (et.default_amount && !form.amount) {
      set('amount', Math.round(et.default_amount).toString())
    }
    if (et.default_wallet_id && !form.wallet_id) {
      set('wallet_id', et.default_wallet_id.toString())
      setWalletPickerOpen(false)
    }
  }

  function handleWalletSelect(walletId: string) {
    set('wallet_id', walletId)
    setWalletPickerOpen(false)
  }

  function handleCreateExpenseType() {
    navigate('/expense-types/new', {
      state: {
        returnTo: returnPath,
        expenseReturnTo: returnTo,
        expenseDraft: form,
      },
    })
  }

  function handleCreateWallet() {
    navigate('/wallets/new', {
      state: {
        returnTo: returnPath,
        expenseReturnTo: returnTo,
        expenseDraft: form,
      },
    })
  }

  async function handleUpdateDefaultAmount(): Promise<void> {
    if (!selectedType) {
      return
    }

    const nextDefaultAmount = form.amount.trim() === '' ? Number.NaN : Number(form.amount)
    if (!Number.isFinite(nextDefaultAmount) || nextDefaultAmount < 0) {
      return
    }

    setToast(null)
    setUpdatingDefaultAmount(true)
    try {
      const updatedExpenseType = await expenseTypeAPI.updateDefaultAmount(
        selectedType.id,
        { default_amount: nextDefaultAmount },
      )
      setExpenseTypes((prev) => prev.map((expenseType) => (
        expenseType.id === updatedExpenseType.id ? updatedExpenseType : expenseType
      )))
      setToast({ kind: 'success', message: t('Default amount updated') })
    } catch (err) {
      setToast({ kind: 'error', message: err instanceof Error ? err.message : t('Failed to update default amount') })
    } finally {
      setUpdatingDefaultAmount(false)
    }
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const payload = {
        expense_type_id: Number(form.expense_type_id),
        wallet_id: form.wallet_id ? Number(form.wallet_id) : null,
        amount: Number(form.amount),
        date: form.date,
        note: form.note || undefined,
      }
      if (isEdit && id) {
        await expenseAPI.update(Number(id), payload)
      } else {
        await expenseAPI.create(payload)
      }
      navigate(returnTo, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Failed to save expense'))
    } finally {
      setLoading(false)
    }
  }

  function handleReturn(): void {
    navigate(returnTo, { replace: true })
  }

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  if (initialLoading) {
    return (
      <div className="page">
        <div className="page__loading"><div className="loading-orb" /></div>
      </div>
    )
  }

  const selectedType = expenseTypes.find((et) => et.id === Number(form.expense_type_id))
  const selectedWallet = wallets.find((wallet) => wallet.id === Number(form.wallet_id))
  const parsedAmount = form.amount.trim() === '' ? null : Number(form.amount)
  const canSubmit = Boolean(
    !loading
    && form.expense_type_id
    && parsedAmount !== null
    && Number.isFinite(parsedAmount)
    && parsedAmount >= 0,
  )
  const canUpdateDefaultAmount = Boolean(
    selectedType
    && parsedAmount !== null
    && Number.isFinite(parsedAmount)
    && parsedAmount >= 0
    && selectedType.default_amount !== parsedAmount,
  )

  return (
    <div className="page">
      {toast && (
        <div
          className={`alert ${toast.kind === 'success' ? 'alert--success' : 'alert--error'}`}
          role={toast.kind === 'error' ? 'alert' : 'status'}
          aria-live="polite"
          style={{
            position: 'fixed',
            left: '50%',
            bottom: '1.5rem',
            transform: 'translateX(-50%)',
            width: 'min(28rem, calc(100vw - 2rem))',
            zIndex: 50,
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.28)',
            backdropFilter: 'blur(12px)',
          }}
        >
          {toast.message}
        </div>
      )}

      <div className="page__header page__header--with-back">
        <button className="back-button" onClick={handleReturn}>
          <ArrowLeftIcon />
        </button>
        <div>
          <h1>{isEdit ? t('Edit Expense') : t('New Expense')}</h1>
          <p>{isEdit ? t('Update expense details') : t('Record a new expense')}</p>
        </div>
      </div>

      <form className="form-card" onSubmit={handleSubmit}>
        {error && <div className="alert alert--error">{error}</div>}

        {/* Expense Type - popup trigger */}
        <div className="field">
          <label className="field__label">{t('Expense Type')} *</label>
          <ExpenseTypePicker
            expenseTypes={expenseTypes}
            selectedTypeId={form.expense_type_id}
            onSelect={handleExpenseTypeSelect}
            onCreateNew={!isEdit ? handleCreateExpenseType : undefined}
            title="Select Expense Type"
          />
        </div>

        {/* Date & Amount */}
        <div className="field-row">
          <div className="field field--flex1">
            <label className="field__label">{t('Date')} *</label>
            <DatePicker
              value={form.date}
              onChange={(value) => set('date', value)}
              required
            />
          </div>
          <div className="field field--flex1">
            <div className="field__label-row">
              <label className="field__label">{t('Amount')} *</label>
              <button
                type="button"
                className="icon-button"
                onClick={() => void handleUpdateDefaultAmount()}
                disabled={!canUpdateDefaultAmount || updatingDefaultAmount}
                title={t('Update default amount')}
                aria-label={t('Update default amount')}
              >
                {updatingDefaultAmount ? <ArrowPathIcon className="spin" /> : <ArrowPathIcon />}
              </button>
            </div>
            <AmountInput
              value={form.amount}
              onChange={(value) => set('amount', value)}
              placeholder={selectedType?.default_amount ? t('Default: {amount}', { amount: Math.round(selectedType.default_amount) }) : '0'}
              title="Expense amount"
            />
          </div>
        </div>

        {/* Wallet - visual grid */}
        <div className="field">
          <div className="field__label-row">
            <label className="field__label">{t('Wallet')}</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {selectedWallet && !walletPickerOpen && (
                <button type="button" className="link-button" onClick={() => setWalletPickerOpen(true)}>
                  {t('Select other')}
                </button>
              )}
              <button type="button" className="link-button" onClick={handleCreateWallet}>
                {t('Add new wallet')}
              </button>
            </div>
          </div>
          {selectedWallet && !walletPickerOpen ? (
            <div className="wallet-select-grid">
              <button
                type="button"
                className="wallet-select-item wallet-select-item--active"
                onClick={() => setWalletPickerOpen(true)}
              >
                <span className="wallet-select-item__icon" style={{ background: selectedWallet.color || '#577590' }}>
                  {selectedWallet.icon || selectedWallet.name.charAt(0).toUpperCase()}
                </span>
                <span className="wallet-select-item__name">{selectedWallet.name}</span>
                {(selectedWallet.is_credit || selectedWallet.is_cash) && (
                  <span className="wallet-select-item__badge">
                    {selectedWallet.is_credit ? t('Credit') : t('Cash')}
                  </span>
                )}
              </button>
            </div>
          ) : (
            <div className="wallet-select-grid">
              <button
                type="button"
                className={`wallet-select-item${form.wallet_id === '' ? ' wallet-select-item--active' : ''}`}
                onClick={() => handleWalletSelect('')}
              >
                <span className="wallet-select-item__icon" style={{ background: '#3d405b' }}>—</span>
                <span className="wallet-select-item__name">{t('None')}</span>
              </button>
              {wallets.map((wallet) => (
                <button
                  key={wallet.id}
                  type="button"
                  className={`wallet-select-item${form.wallet_id === wallet.id.toString() ? ' wallet-select-item--active' : ''}`}
                  onClick={() => handleWalletSelect(wallet.id.toString())}
                >
                  <span className="wallet-select-item__icon" style={{ background: wallet.color || '#577590' }}>
                    {wallet.icon || wallet.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="wallet-select-item__name">{wallet.name}</span>
                  {(wallet.is_credit || wallet.is_cash) && (
                    <span className="wallet-select-item__badge">
                      {wallet.is_credit ? t('Credit') : t('Cash')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          <p className="field-hint">{t('Link this expense to a wallet for billing tracking')}</p>
        </div>

        {/* Note */}
        <div className="field">
          <label className="field__label">{t('Note')}</label>
          <input
            className="field__input"
            value={form.note}
            onChange={(e) => set('note', e.target.value)}
            placeholder={t('Optional note')}
          />
        </div>

        {/* Preview */}
        {selectedType && (
          <div className="form-preview">
            <div
              className="entity-card__icon"
              style={{ background: selectedType.color || '#577590' }}
            >
              {selectedType.icon || selectedType.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <strong>{selectedType.name}</strong>
              <span className="form-preview__sub">
                {parsedAmount !== null && Number.isFinite(parsedAmount) ? formatCurrency(parsedAmount) : t('No amount')}
                {form.date && ` · ${formatDate(form.date)}`}
                {form.wallet_id && (() => {
                  const w = wallets.find((w) => w.id === Number(form.wallet_id))
                  return w ? ` · ${w.icon} ${w.name}` : ''
                })()}
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="form-actions">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={!canSubmit}
          >
            {loading ? t('Saving…') : isEdit ? t('Update Expense') : t('Create Expense')}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={handleReturn}
          >
            {t('Cancel')}
          </button>
        </div>
      </form>
    </div>
  )
}
